import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  useErrorHandlerContext,
  useOpeningElementsControllerContext,
  useUserLanguage,
} from '@/customHooks'
import { rentChargeStatusMapper, truncateDisplayId } from '@/helpers'
import {
  GetExpensesReportQuery,
  ExpensesReportFilterInput,
  RentChargeStatusOption,
  useGetExpensesReportQuery,
  useUpdateExpenseStatusMutation,
} from '@/graphql/generated'
import { LoadingSpinner, RentChargeStatusOptionPill } from '../atoms'
import { NormalFormSelectionField } from '../molecules'
import textsData from '@/textData/reportTexts.json'
import { GET_EXPENSES_REPORT } from '@/graphql/queries/getExpensesReport'
import { CenterScreenModal } from '../atoms'
import { ConfirmationDialog } from './ConfirmationDialog'
import { pathURL } from '@/components/constants/urls'

type ExpensesForList =
  NonNullable<GetExpensesReportQuery['expensesReport']>['items'][number]

const readableDate = (
  date: Date | string | null | undefined,
  userLanguage: 'es' | 'en'
) => {
  if (!date) return '-'
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateObj.toLocaleDateString(
      userLanguage === 'es' ? 'es-ES' : 'en-US',
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }
    )
  } catch {
    return '-'
  }
}

const formatAmount = (
  amount: string,
  currency: string,
  userLanguage: 'es' | 'en'
) => {
  const numAmount = parseFloat(amount)
  if (isNaN(numAmount)) return amount
  return new Intl.NumberFormat(userLanguage === 'es' ? 'es-ES' : 'en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
  }).format(numAmount)
}

const sanitizePayerLabel = (payerLabel?: string | null) => {
  if (!payerLabel) return '-'

  const withoutPercentage = payerLabel.replace(/\b\d+(?:[.,]\d+)?\s*%/gi, '')

  const cleaned = payerLabel
    .replace(/\b\d+(?:[.,]\d+)?\s*%/gi, '')
    .replace(
      /\b(owner|supplier|tenant|propietario|proveedor|inquilino)\b/gi,
      ''
    )
    .replace(/[()]/g, ' ')
    .replace(/\s*[-|,]\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  if (cleaned) return cleaned

  const fallback = withoutPercentage
    .replace(/[()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  return fallback || '-'
}

type ExpensesListTableProps = {
  filters: ExpensesReportFilterInput;
}

export const ExpensesListTable = ({ filters }: ExpensesListTableProps) => {
  const navigate = useNavigate()
  const userLanguage = useUserLanguage()
  const texts =
    (userLanguage === 'es' ? textsData.es : textsData.en).expenseReport

  const { open: errorHandle } = useErrorHandlerContext()
  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [pendingExpenseId, setPendingExpenseId] = useState<string | null>(null)
  const [selectedStatusByExpense, setSelectedStatusByExpense] = useState<
    Record<string, RentChargeStatusOption | null>
  >({})
  const { data, error, loading } = useGetExpensesReportQuery({
    variables: {
      filterInput: filters,
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })
  const [updateExpenseStatus, { loading: updateLoading }] =
    useUpdateExpenseStatusMutation({
      refetchQueries: [{ query: GET_EXPENSES_REPORT }],
    })

  const statusOptions = useMemo(
    () =>
      Object.values(RentChargeStatusOption).map((value) => ({
        name: rentChargeStatusMapper(value, userLanguage),
        value,
      })),
    [userLanguage]
  )

  const dataFlatter = (data: ExpensesForList[] | undefined) => {
    if (!data) return []

    return data.map((element) => ({
      id: element.id,
      title: element.title || '-',
      description: element.description || '-',
      date: element.date,
      property: element.propertyName || '-',
      supplier: element.supplierName || '-',
      incidentId: element.incidentId || null,
      payer: sanitizePayerLabel(element.payerLabel),
      amount: formatAmount(element.expenseCost, element.currencyOption, userLanguage),
      chargeStatus: element.chargeStatus,
    }))
  }

  const flattenedData = useMemo(() => {
    if (!data) return null
    return dataFlatter(data.expensesReport?.items)
  }, [data, userLanguage])

  const handleToggleExpand = useCallback(
    (expenseId: string, status: RentChargeStatusOption | null) => {
      if (expandedId === expenseId) {
        setExpandedId(null)
        return
      }

      setExpandedId(expenseId)
      setSelectedStatusByExpense((prev) => ({
        ...prev,
        [expenseId]: prev[expenseId] ?? status,
      }))
    },
    [expandedId]
  )

  const handleStatusChange = useCallback(
    (expenseId: string, status: RentChargeStatusOption) => {
      setSelectedStatusByExpense((prev) => ({
        ...prev,
        [expenseId]: status,
      }))
    },
    []
  )

  const executeStatusUpdate = useCallback(
    async (expenseId: string) => {
      const selectedStatus = selectedStatusByExpense[expenseId]
      if (!selectedStatus) return

      try {
        await updateExpenseStatus({
          variables: {
            updateExpenseStatusInput: {
              id: expenseId,
              chargeStatus: selectedStatus,
            },
          },
        })
        confirmDialogModalOpen({
          title: userLanguage === 'es' ? 'Estado actualizado' : 'Status updated',
          description:
            userLanguage === 'es'
              ? 'El estado del gasto se actualizo correctamente.'
              : 'Expense status was updated successfully.',
        })
        setExpandedId(null)
      } catch (mutationError) {
        errorHandle(mutationError)
      }
    },
    [
      confirmDialogModalOpen,
      errorHandle,
      selectedStatusByExpense,
      updateExpenseStatus,
      userLanguage,
    ]
  )

  const handleConfirmStatus = useCallback((expenseId: string) => {
    setPendingExpenseId(expenseId)
  }, [])

  const handleAcceptStatusChange = useCallback(async () => {
    if (!pendingExpenseId) return
    await executeStatusUpdate(pendingExpenseId)
    setPendingExpenseId(null)
  }, [executeStatusUpdate, pendingExpenseId])

  const handleCancelStatusChange = useCallback(() => {
    setPendingExpenseId(null)
  }, [])

  if (error) errorHandle(error)

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner text />
      </div>
    )
  }

  if (!flattenedData || flattenedData.length === 0) {
    return (
      <div className="w-full py-4 text-center">
        <span className="font-bold">{texts.noData.table}</span>
      </div>
    ) 
  }

  const onIncidentClick = (incidentId: string) => {
    navigate(`/${pathURL.USER}/${pathURL.INCIDENTS}/${pathURL.DETAIL}`, {
      state: { id: incidentId },
    })
  }

  return (
    <div className="pt-4 pb-4 rounded-xl grid grid-cols-[2fr_1.2fr_1.8fr_1.8fr_1.2fr_1.2fr_70px] overflow-x-auto min-w-[900px]">
      {/* Header */}
      <div className="contents">
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {texts.expensesTable.title}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {texts.expensesTable.date}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {texts.expensesTable.property}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {texts.expensesTable.payer}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {texts.expensesTable.amount}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {texts.expensesTable.status}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white" />
      </div>

      {flattenedData.map((expense, index) => {
        const rowBg = index % 2 !== 0 ? 'bg-background2' : 'bg-white'
        const isExpanded = expandedId === expense.id
        const selectedStatus = selectedStatusByExpense[expense.id] ?? expense.chargeStatus

        return (
          <div key={expense.id} className="contents">
            <div
              onClick={() => handleToggleExpand(expense.id, expense.chargeStatus)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer ${rowBg}`}
            >
              {expense.title}
            </div>
            <div
              onClick={() => handleToggleExpand(expense.id, expense.chargeStatus)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer ${rowBg}`}
            >
              {readableDate(expense.date, userLanguage)}
            </div>
            <div
              onClick={() => handleToggleExpand(expense.id, expense.chargeStatus)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer ${rowBg}`}
            >
              {expense.property}
            </div>
            <div
              onClick={() => handleToggleExpand(expense.id, expense.chargeStatus)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer ${rowBg}`}
            >
              {expense.payer}
            </div>
            <div
              onClick={() => handleToggleExpand(expense.id, expense.chargeStatus)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer ${rowBg}`}
            >
              {expense.amount}
            </div>
            <div
              onClick={() => handleToggleExpand(expense.id, expense.chargeStatus)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer ${rowBg}`}
            >
              {expense.chargeStatus ? (
                <RentChargeStatusOptionPill option={expense.chargeStatus} />
              ) : (
                '-'
              )}
            </div>
            <div
              onClick={() => handleToggleExpand(expense.id, expense.chargeStatus)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center justify-center cursor-pointer ${rowBg}`}
            >
              <svg
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {isExpanded && (
              <div className="col-start-1 col-end-8 bg-gray-50 pb-4 border-b border-gray-200">
                <div className="bg-white mx-4 mt-2 rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-bold text-primary2">
                        {userLanguage === 'es' ? 'ID del gasto' : 'Expense ID'}
                      </h4>
                      <p className="mt-1 text-sm text-gray-700" title={expense.id}>
                        {truncateDisplayId(expense.id)}
                      </p>
                    </div>
                    <div>
                      <h4 className="font-bold text-primary2">
                        {userLanguage === 'es' ? 'Descripcion' : 'Description'}
                      </h4>
                      <p className="mt-1 text-sm text-gray-700">
                        {expense.description}
                      </p>
                    </div>
                  </div>

                  <div className="mb-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h4 className="font-bold text-primary2 border-b border-gray-200 pb-2">
                      {userLanguage === 'es' ? 'Datos adicionales' : 'Additional data'}
                    </h4>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-semibold text-gray-700">
                          {userLanguage === 'es' ? 'Origen' : 'Origin'}
                        </p>
                        {expense.incidentId ? (
                          <button
                            type="button"
                            className="bg-transparent border-none p-0 m-0 text-primary1 underline cursor-pointer hover:text-primary1/80"
                            onClick={() => onIncidentClick(expense.incidentId!)}
                          >
                            {truncateDisplayId(expense.incidentId)}
                          </button>
                        ) : (
                          <p>-</p>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-700">
                          {userLanguage === 'es'
                            ? 'Proveedor responsable'
                            : 'Responsible supplier'}
                        </p>
                        <p>{expense.supplier}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <h4 className="font-bold text-primary2 border-b border-gray-100 pb-2">
                      {userLanguage === 'es'
                        ? 'Cambio de estado'
                        : 'Status change'}
                    </h4>
                  </div>
                  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 max-w-2xl">
                    <div className="flex-1">
                      <NormalFormSelectionField<RentChargeStatusOption>
                        options={statusOptions}
                        name={`status-${expense.id}`}
                        value={selectedStatus}
                        label={userLanguage === 'es' ? 'Estado del gasto' : 'Expense status'}
                        onChange={(value) => handleStatusChange(expense.id, value)}
                      />
                    </div>
                    <button
                      onClick={() => handleConfirmStatus(expense.id)}
                      disabled={
                        updateLoading ||
                        !selectedStatus ||
                        selectedStatus === expense.chargeStatus
                      }
                      className="bg-primary1 text-white text-xs font-bold h-[42px] px-6 rounded hover:opacity-90 transition-opacity disabled:opacity-50 md:mb-[15px]"
                    >
                      {updateLoading
                        ? userLanguage === 'es'
                          ? 'Guardando...'
                          : 'Saving...'
                        : userLanguage === 'es'
                          ? 'Aceptar'
                          : 'Confirm'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      })}

      <CenterScreenModal
        isOpen={pendingExpenseId !== null}
        onClose={handleCancelStatusChange}
      >
        <ConfirmationDialog
          title={
            userLanguage === 'es'
              ? 'Confirmar cambio de estado'
              : 'Confirm status change'
          }
          description={
            userLanguage === 'es'
              ? 'Estas seguro de cambiar el estado de este gasto?'
              : 'Are you sure you want to change this expense status?'
          }
          confirmButton={{
            action: handleAcceptStatusChange,
            text: userLanguage === 'es' ? 'Si, cambiar' : 'Yes, change',
          }}
          cancelButton={{
            action: handleCancelStatusChange,
            text: userLanguage === 'es' ? 'Cancelar' : 'Cancel',
          }}
        />
      </CenterScreenModal>
    </div>
  )
}

